/**
 * What-If HOME canvas state (design doc executive-view-design/
 * EXEC_WHATIF_v1_design.md §4 HOME row, §5 shared Examples/Recent
 * grammar).
 *
 * Heading + a single-column list of compact selectable rows, under a
 * tab control (EXAMPLES | RECENT (n)). Row treatment mirrors
 * QuestionLibrary.tsx's outlined-card rows and its small-caps
 * section-label typography, so the two library-style surfaces read as
 * one system.
 *
 * Tab-visibility rule (locked, §4): the tab control is not merely
 * defaulted, it is removed from the DOM entirely when there are no
 * recents — "zero-recents is pixel-identical" to a single-list screen.
 * EXAMPLES is always the default tab (ruled 2026-08-23; the doc's
 * original "RECENT once history exists" one-shot default is gone).
 *
 * Tab selection is controlled by the parent (WhatIfView), not owned
 * here: this component unmounts/remounts on every home ↔ non-home
 * canvas transition (it's conditionally rendered on `canvas.state ===
 * 'home'`), so any local tab state — including a "default to RECENT
 * once, never again" latch — would reset on every round trip through
 * CONFIRM and back. WhatIfView stays mounted across canvas transitions,
 * so the tab value and its one-shot default latch live there instead;
 * this component just renders whatever `activeTab` it's given and
 * reports clicks via `onChangeTab`.
 *
 * Row click semantics: an example seeds the form and the parent flips
 * to CONFIRM (design doc HOME row "Exits"). Every recent row navigates
 * via WhatIfView.handleOpenRun by the run's FETCHED status (2026-08-23
 * amendment): completed → RESULT, executing → RECALCULATING, pending →
 * the live PROPOSAL, anything else → CONFIRM carrying this file's
 * statusLine copy as the notice. Each row declares its state via a
 * StatusChip in the caption (2026-08-23; replaced the inline status
 * sentence); cancelled/superseded rows never arrive — the backend
 * recents query hides them.
 */
import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowForward, DeleteOutline } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';

import {
  renderPromptTitle,
  stripPromptMarkdown,
} from 'src/components/shared/PromptTitle';

import { formatUtcTimestamp } from '../formatUtcTimestamp';
import { ExecutiveBrand, WhatIfExample, WhatIfRecentRun } from '../types';

export type HomeTab = 'examples' | 'recent';

interface HomeStateProps {
  brand: ExecutiveBrand;
  examples: WhatIfExample[];
  recents: WhatIfRecentRun[];
  recentsLoading: boolean;
  // Controlled by WhatIfView (see file header) — this component neither
  // stores nor defaults the tab itself.
  activeTab: HomeTab;
  onChangeTab: (tab: HomeTab) => void;
  onSelectExample: (example: WhatIfExample) => void;
  onSelectRecent: (recent: WhatIfRecentRun) => void;
  // Soft-hide a FAILED run (ruled 2026-08-23) — WhatIfView owns the
  // API call + list removal; rejects on failure so the confirm flow
  // here can surface it.
  onDismissRecent: (recent: WhatIfRecentRun) => Promise<void>;
}

const SECTION_LABEL_SX = {
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  fontWeight: 600,
} as const;

// Reopen-notice copy, in the executive's vocabulary (ruled 2026-08-21).
// Used by WhatIfView as the CONFIRM notice when a reopened run has no
// screen of its own to land on. Cancelled/superseded rows no longer
// appear in the recents list at all (ruled 2026-08-23 — the backend
// excludes them), but their sentences stay: a row's status can change
// between listing and click (e.g. superseded from another tab), and the
// reopen fetch still needs calm words for what it finds.
export const statusLine = (
  status: WhatIfRecentRun['status']
): string | null => {
  switch (status) {
    case 'cancelled':
    case 'superseded':
      return 'Cancelled — nothing was applied.';
    case 'failed':
      return 'This run did not complete.';
    case 'executing':
    case 'pending_diff_confirmation':
      return 'Still in progress.';
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Status chips (ruled 2026-08-23): every recent row declares its state
// via a small tinted chip embedded in the caption line — the five
// user-facing statuses, GA-list-style soft tints. The WORD carries the
// meaning (a11y: color is reinforcement, never the only channel); every
// ink clears 4.5:1 small-text contrast on its own tint (validated
// 5.4–5.9:1, dataviz six-checks session 2026-08-23). Cancelled and
// superseded are absent by design — the backend hides those rows.
// ---------------------------------------------------------------------------
interface StatusChipSpec {
  label: string;
  fg: string;
  bg: string;
}

const CHIP_AMBER = { fg: '#7a5300', bg: '#fbeab8' };
const CHIP_BLUE = { fg: '#184f95', bg: '#c7dffb' };
const CHIP_GREEN = { fg: '#006300', bg: '#cceccc' };
const CHIP_SALMON = { fg: '#9e3316', bg: '#fcd9cc' };

const statusChip = (recent: WhatIfRecentRun): StatusChipSpec | null => {
  switch (recent.status) {
    case 'pending_diff_confirmation':
      return { label: 'Pending approval', ...CHIP_AMBER };
    case 'executing':
      // The backend's phase discriminator; a missing phase (older
      // cached row) defaults to the pre-approve reading.
      return recent.phase === 'recalculating'
        ? { label: 'Recalculating', ...CHIP_BLUE }
        : { label: 'Preparing changes', ...CHIP_BLUE };
    case 'completed':
      return { label: 'Completed', ...CHIP_GREEN };
    case 'failed':
      return { label: 'Failed', ...CHIP_SALMON };
    default:
      // cancelled/superseded never arrive (backend-filtered); if one
      // slips through a stale cache, no chip beats a wrong chip.
      return null;
  }
};

const StatusChip: React.FC<{ spec: StatusChipSpec }> = ({ spec }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      ml: 1,
      px: 1,
      py: '1px',
      borderRadius: '999px',
      bgcolor: spec.bg,
      color: spec.fg,
      fontSize: '0.75rem',
      fontWeight: 500,
      lineHeight: 1.6,
      verticalAlign: 'text-bottom',
      whiteSpace: 'nowrap',
    }}
  >
    {spec.label}
  </Box>
);

const HomeState: React.FC<HomeStateProps> = ({
  brand,
  examples,
  recents,
  recentsLoading,
  activeTab,
  onChangeTab,
  onSelectExample,
  onSelectRecent,
  onDismissRecent,
}) => {
  const hasRecents = recents.length > 0;
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  // Remove-from-list flow (failed rows only): standard confirm dialog
  // (same material-ui-confirm idiom as the analyst library's deletes),
  // then the parent's dismiss. Failure keeps the row and says so calmly.
  // Title only — a body line restating "failed" earned its removal
  // (ruled 2026-08-23: redundant).
  const handleRemove = (recent: WhatIfRecentRun) => {
    confirm({
      title: 'Remove this failed run?',
      confirmationText: 'Remove',
    })
      .then(async () => {
        try {
          await onDismissRecent(recent);
        } catch {
          enqueueSnackbar("Couldn't remove the run. Please try again.", {
            variant: 'error',
          });
        }
      })
      .catch(() => undefined);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Ask a what-if question about {brand.model_name}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Pick an example or pick up a recent analysis, then adjust it below.
      </Typography>

      {hasRecents && (
        <Tabs
          value={activeTab}
          onChange={(_e, next: HomeTab) => onChangeTab(next)}
          sx={{ mb: 2, minHeight: 36 }}
        >
          <Tab
            value="examples"
            label="Examples"
            sx={{ minHeight: 36, py: 0.5 }}
          />
          <Tab
            value="recent"
            label={`Recent (${recents.length})`}
            sx={{ minHeight: 36, py: 0.5 }}
          />
        </Tabs>
      )}

      {!hasRecents && (
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}
        >
          Examples
        </Typography>
      )}

      {activeTab === 'recent' && recentsLoading && (
        <Typography variant="body1" color="text.secondary">
          Loading recent analyses…
        </Typography>
      )}

      {activeTab === 'examples' && examples.length === 0 && (
        <Typography variant="body1" color="text.secondary">
          No example questions are configured for this brand yet. Please contact
          your Trinity team.
        </Typography>
      )}

      {!(activeTab === 'recent' && recentsLoading) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {activeTab === 'examples' &&
            examples.map((example) => (
              <Card key={example.id} variant="outlined">
                <CardActionArea onClick={() => onSelectExample(example)}>
                  <CardContent
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1.5,
                      '&:last-child': { pb: 1.5 },
                    }}
                  >
                    <Typography variant="body1">
                      {renderPromptTitle(example.title)}
                    </Typography>
                    <ArrowForward
                      fontSize="small"
                      sx={{ color: 'text.secondary', ml: 2, flexShrink: 0 }}
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}

          {activeTab === 'recent' &&
            recents.map((recent) => {
              const chip = statusChip(recent);
              return (
                // Two-zone row: the reopen click target (CardActionArea)
                // and, on failed rows only, a separate remove zone. The
                // IconButton must live OUTSIDE the action area — a
                // button nested in the row's button is invalid HTML and
                // an a11y trap (two tab stops, ambiguous activation).
                <Card key={recent.plan_id} variant="outlined">
                  <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <CardActionArea
                      onClick={() => onSelectRecent(recent)}
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <CardContent
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 1.5,
                          '&:last-child': { pb: 1.5 },
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body1">
                            {renderPromptTitle(recent.title)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {recent.scenario_name}
                            {' · '}
                            {formatUtcTimestamp(recent.inserted_at)}
                            {chip && <StatusChip spec={chip} />}
                          </Typography>
                        </Box>
                        <ArrowForward
                          fontSize="small"
                          sx={{ color: 'text.secondary', ml: 2, flexShrink: 0 }}
                        />
                      </CardContent>
                    </CardActionArea>
                    {recent.status === 'failed' && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          pl: 0.5,
                          pr: 1,
                          flexShrink: 0,
                        }}
                      >
                        <Tooltip title="Remove from list">
                          <IconButton
                            size="small"
                            aria-label={`Remove failed run: ${stripPromptMarkdown(recent.title)}`}
                            onClick={() => handleRemove(recent)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Card>
              );
            })}
        </Box>
      )}
    </Box>
  );
};

export default HomeState;
